<template>
  <div
    :class="{ fixed: isFixed }"
    class="ai-filters">
    <div class="ai-filters-title">Key Considerations</div>
    <div 
      v-if="skeletonLoading"
      class="ai-filters-wrapper skeleton">
      <div class="filter-item first"></div>
      <div class="filter-item-list">
        <div v-for="item in 10" :key="item" class="filter-item"></div>
      </div>
    </div>
    <div 
      v-else  
      class="ai-filters-wrapper">
      <div 
        @click="showFullFilterPopDialog"
        :class="{ checked: hasChecked }"
        class="filter-item first">
        <Icon 
          class="icon" 
          name="icon-filter" />
      </div>
      <div class="filter-item-list">
        <div 
          @click="handleFilterItemClick(item)"
          v-for="(item, index) in filterList" 
          :key="index" 
          :class="{ checked: item.checked }"
          class="filter-item">
          <div class="filter-item-attribute">
            {{ formatFilterItemTitle(item.title) }}
          </div>
          <Icon name="arrow-down-l-2" />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <Transition name="fade">
      <div 
        v-if="popDialogVisible" 
        @click="closePopDialog"
        class="pop-dialog">
        <div 
          ref="popDialogRef"
          @click.stop
          class="pop-dialog-content">
          <div class="header">
            {{ formatFilterItemTitle(currentFilterItem.title) }}
            <img @click="closePopDialog" src="@/assets/images/close-icon.svg" alt="">
          </div>
          <div class="attribute-list">
            <div 
              v-for="(item, index) in currentFilterItem.attributes" 
              :key="index">
              <label class="attribute-item" :for="item.value">
                {{ item.en }}
                <input 
                  :id="item.value"
                  v-model="item.default" 
                  type="checkbox" />
              </label>
            </div>
          </div>
          <div class="footer">
            <div @click="handleReset" class="button-reset">Reset</div>
            <div @click="handleDone" class="button-done">Done</div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <Teleport to="body">
    <!-- 底部弹出动画 -->
    <Transition name="bottom-fade">
      <div v-if="fullFilterPopDialogVisible" class="full-filter-pop-dialog">
        <div class="full-filter-pop-dialog-content">
          <div class="header">
            <div class="header-title">Filters</div>
            <img @click="closeFullFilterPopDialog" src="@/assets/images/close-icon.svg" alt="">
          </div>
          <div class="body">
            <div 
              v-for="(item, index) in filterList" 
              :key="index"
              class="filter-wrapper">
              <div class="filter-header">
                {{ formatFilterItemTitle(item.title) }}
                <!-- 收起 -->
                <Icon v-if="item.isExpand" @click="handleCollapse(item, index)" name="reduce-l"></Icon>
                <!-- 展开 -->
                <Icon v-else @click="handleExpand(item, index)" name="add-l"></Icon>
              </div>
              <div 
                ref="filterItemListRefList"
                class="filter-item-list">
                <div 
                  v-for="(attribute, index) in item.attributes" 
                  :key="index"
                  class="filter-item">
                  <div class="filter-item-attribute">
                    {{ attribute.en }}
                    <label class="attribute-item" :for="attribute.value">
                      <input 
                        :id="attribute.value"
                        v-model="attribute.default" 
                        type="checkbox" />
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="footer">
              <div @click="handleFullFilterReset" class="button-reset">Reset</div>
              <div @click="handleFullFilterDone" class="button-done">Done</div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@/components/Icon/Icon.vue';

const props = defineProps({
  filterList: {
    type: Array,
    default: () => []
  },
  // 骨架屏loading
  skeletonLoading: {
    type: Boolean,
    default: false
  },
  isFixed: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['update'])

const popDialogRef = ref(null)
const currentFilterItem = ref(null)
const originalFilterItem = ref(null) // 原始数据
const popDialogVisible = ref(false)

const fullFilterPopDialogVisible = ref(false) // 总筛选弹窗
const filterItemListRefList = ref(null)
const originalFilterList = ref([])

const hasChecked = computed(() => {
  return props.filterList.some(item => item.checked)
})

watch(popDialogVisible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const dom = popDialogRef.value
      dom.style.height = 'auto'
      const h = dom.offsetHeight
      dom.style.height = 0
      dom.offsetHeight
      dom.style.height = `${h}px`
    })
  }
})

// 首字母大写
const formatFilterItemTitle = (title) => {
  return title.charAt(0).toUpperCase() + title.slice(1)
}

// 执行关闭动画
const closePopDialogAnimation = () => {
  const dom = popDialogRef.value
  dom.style.height = 0
}

const closePopDialog = () => {
  closePopDialogAnimation()
  setTimeout(() => {
    popDialogVisible.value = false
    handleReset()
  }, 300)
}

// 打开弹窗并记录原始数据
const handleFilterItemClick = (item) => {
  console.log(item)
  originalFilterItem.value = JSON.parse(JSON.stringify(item))
  currentFilterItem.value = item
  popDialogVisible.value = true
}

// 恢复原始数据
const handleReset = () => {
  props.filterList.forEach(item => {
    if (item.title === originalFilterItem.value.title) {
      item.attributes.forEach((attribute, index) => {
        attribute.default = originalFilterItem.value.attributes[index].default
      })
    }
  })
}

const handleDone = () => {
  closePopDialogAnimation()
  setTimeout(() => {
    let keywords = ''
    props.filterList.forEach(item => {
      const attributes = item.attributes
      attributes.forEach(item => {
        if (item.default) {
          keywords += `${item.cn}`
        }
      })
    })
    popDialogVisible.value = false
    emits('update', keywords)
  }, 300)
}

const showFullFilterPopDialog = () => {
  originalFilterList.value = JSON.parse(JSON.stringify(props.filterList))
  fullFilterPopDialogVisible.value = true
}

const closeFullFilterPopDialog = () => {
  handleFullFilterReset()
  fullFilterPopDialogVisible.value = false
}

const handleCollapse = (item, index) => {
  item.isExpand = false
  const dom = filterItemListRefList.value[index]
  dom.style.height = 'auto'
  const h = dom.offsetHeight
  dom.style.height = `${h}px`
  dom.offsetHeight
  dom.style.height = 0
  dom.style.marginBottom = 0
}

const handleExpand = (item, index) => {
  item.isExpand = true
  const dom = filterItemListRefList.value[index]
  dom.style.height = 'auto'
  dom.style.marginBottom = 16 + 'px'
  const h = dom.offsetHeight
  dom.style.height = 0
  dom.offsetHeight
  dom.style.height = `${h}px`
}

const handleFullFilterReset = () => {
  props.filterList.forEach((item, index) => {
    item.attributes = originalFilterList.value[index].attributes
  })
}

const handleFullFilterDone = () => {
  let keywords = ''
  props.filterList.forEach(item => {
    const attributes = item.attributes
    attributes.forEach(item => {
      if (item.default) {
        keywords += `${item.cn}`
      }
    })
  })
  fullFilterPopDialogVisible.value = false
  emits('update', keywords)
}

onMounted(() => {
  window.addEventListener('scroll', () => {
    console.log(window.scrollY)
  })
})

</script>

<style lang='less' scoped>
.fixed {
  position: fixed;
  top: 60px;
  z-index: 999;
  width: 100%;
  padding-bottom: 12px;
  background: white
}

.ai-filters {
  margin-left: 16px;
  .ai-filters-title {
    font-size: 16px;
    font-family: mon-600;
    color: #232323;
    margin-top: 16px;
    margin-bottom: 12px;
  }

  .ai-filters-wrapper {
    display: flex;
    gap: 8px;

    &.skeleton {
      .filter-item {
        width: 94px;
        flex-shrink: 0;
        animation: skeleton-loading 2s infinite ease-in-out;
      }

      @keyframes skeleton-loading {
        0% {
          background-color: #f8f8f8;
        }
        50% {
          background-color: #ebebeb;
        }
        100% {
          background-color: #f8f8f8;
        }
      }
    }
    
    .filter-item-list {
      display: flex;
      gap: 8px;
      overflow: scroll;
      padding-right: 24px;
      &::-webkit-scrollbar {
        display: none;
      }
    }

    .filter-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      height: 36px;
      padding: 0 16px;
      border-radius: 20px;
      background: #f8f8f8;
      color: #232323;
      font-family: mon-600;
      font-size: 12px;
      &.first {
        width: 36px;
        border-radius: 50%;
        .icon {
          transform: scale(5);
        }
      }

      &.checked {
        background: #052784;
        color: #fff;
      }

      .icon {
        font-size: 20px;
      }
    }
  }
}

.pop-dialog {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  z-index: 99999;
  .pop-dialog-content {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 12px;
    background-color: white;
    border-radius: 8px;
    padding: 0 16px;
    transition: height 0.3s ease;

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 52px;
      color: #5c5c5c;
      font-size: 12px;
      font-family: mon-600;
    }

    .attribute-list {
      margin-bottom: 24px;
      .attribute-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 52px;
        color: #232323;
        font-size: 12px;
        font-family: mon-500;
        input[type="checkbox"] {
          position: relative;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 1px solid #d9d9d9;
          &:checked {
            background-color: #000;
            border: 1px solid #000;
          }
          &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: url('@/assets/H5/filters/check.svg') no-repeat center center;
          }
        }
      }
    }

    .footer {
      display: flex;
      justify-content: center;
      gap: 9px;
      border-top: 0.5px solid #e4e6eb;
      padding: 8px 16px 0;
      height: 64px;

      .button-reset, 
      .button-done {
        width: 50%;
        height: 40px;
        line-height: 40px;
        text-align: center;
        border-radius: 20px;
        font-size: 14px;
        font-family: mon-600;
        color: #232323;
      }

      .button-reset {
        border: 0.5px solid #b2b7c2;
      }

      .button-done {
        background: #052784;
        color: #fff;
      }
    }
  }
}

.full-filter-pop-dialog {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  padding: 12px 16px;
  z-index: 99999;

  .full-filter-pop-dialog-content {
    height: 100%;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    background-color: white;
    border-radius: 8px;
    padding: 0 12px;
    color: #232323;
    font-size: 14px;
    font-family: mon-600;
    margin-bottom: 12px;
  }

  .body {
    height: calc(100% - 60px);
    background-color: white;
    border-radius: 8px;
    padding: 8px 16px;
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }

    .filter-wrapper {
      &:first-child .filter-header {
        border-top: none;
      }

      .filter-header {
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-top: 0.5px solid #e4e6eb;
        font-size: 12px;
        color: #232323;
        font-family: mon-600;
      }

      .filter-item-list {
        overflow: hidden;
        transition: height 0.3s ease;
        margin-bottom: 16px;
      }

      .filter-item {
        display: flex;
        align-items: center;
        height: 42px;
        font-size: 14px;
        font-family: mon-500;
        color: #232323;
        .filter-item-attribute {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          input[type="checkbox"] {
          position: relative;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 1px solid #d9d9d9;
          &:checked {
            background-color: #000;
            border: 1px solid #000;
          }
          &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: url('@/assets/H5/filters/check.svg') no-repeat center center;
          }
        }
        }
      }
    }

    .footer {
      position: fixed;
      bottom: 12px;
      left: 16px;
      right: 16px;
      height: 64px;
      border-top: 0.5px solid #e4e6eb;
      padding: 8px 16px 0;
      display: flex;
      justify-content: center;
      gap: 9px;
      background-color: white;
      border-radius: 0 0 8px 8px;
      .button-reset, 
      .button-done {
        width: 50%;
        height: 40px;
        line-height: 40px;
        text-align: center;
        border-radius: 20px;
        font-size: 14px;
        font-family: mon-600;
        color: #232323;
      }

      .button-reset {
        border: 0.5px solid #b2b7c2;
      }

      .button-done {
        background: #052784;
        color: #fff;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.bottom-fade-enter-active,
.bottom-fade-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.bottom-fade-enter-from,
.bottom-fade-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>